import React, { useState } from 'react';
import {
    Card,
    Group,
    Select,
    NumberInput,
    ActionIcon,
    Text,
    TextInput,
    Stack,
    Button,
    Divider,
} from '@mantine/core';
import {
    IconTrash,
    IconChevronUp,
    IconChevronDown,
    IconQrcode,
    IconX,
    IconPlus,
    IconRefresh,
} from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import type { PanelContentWithDuration, QrCodeConfig } from '../../data/DeviceConfig';
import { COMPONENT_OPTIONS, COMPONENT_REGISTRY } from './componentRegistry';
import { apiClient } from '../../data/apiClient';
import { useCsrf } from '../../hooks/useCsrf';

interface CarouselItemEditorProps {
    item: PanelContentWithDuration;
    index: number;
    totalItems: number;
    onChange: (item: PanelContentWithDuration) => void;
    onRemove: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
}

const DEFAULT_QR_CODE: QrCodeConfig = {
    x: '0px',
    y: '0px',
    size: '100px',
    marginSize: 1,
    target: 'https://',
    utmParams: {},
};

type UtmPair = { id: string; key: string; value: string };

const CarouselItemEditor: React.FC<CarouselItemEditorProps> = ({
    item,
    index,
    totalItems,
    onChange,
    onRemove,
    onMoveUp,
    onMoveDown,
}) => {
    const [utmPairs, setUtmPairs] = useState<UtmPair[]>(() =>
        Object.entries(item.qrCode?.utmParams || {}).map(([key, value], i) => ({
            id: `${i}-${key}`,
            key,
            value,
        }))
    );
    const [isReloading, setIsReloading] = useState(false);
    const { deviceId } = useParams();
    const { csrfToken } = useCsrf();

    const componentDef = COMPONENT_REGISTRY[item.component];
    const params = (item.params as Record<string, any>) || {};

    // Live iframe panels (URL Marquee) can be reloaded on the device without a config
    // change: this asks the device to remount just the iframe serving this contentUrl.
    const isUrlMarquee = item.component === 'UrlMarquee';
    const marqueeUrl = typeof params.contentUrl === 'string' ? params.contentUrl.trim() : '';

    const handleRefreshIframe = async () => {
        if (!deviceId || !marqueeUrl) return;
        setIsReloading(true);
        try {
            await apiClient.post(
                `api/device/${deviceId}/reload-content`,
                { contentUrl: marqueeUrl },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': csrfToken,
                    },
                }
            );
            notifications.show({
                title: 'Refresh requested',
                message: 'The device was asked to reload this iframe.',
            });
        } catch (err: unknown) {
            notifications.show({
                title: 'Refresh failed',
                message: err instanceof Error ? err.message : 'Could not refresh the iframe.',
                color: 'red',
            });
        } finally {
            setIsReloading(false);
        }
    };

    // If the component name isn't in the registry, still show it in the Select
    const componentOptions = COMPONENT_OPTIONS.find(o => o.value === item.component)
        ? COMPONENT_OPTIONS
        : [...COMPONENT_OPTIONS, { value: item.component, label: item.component }];

    const handleComponentChange = (value: string | null) => {
        if (!value) return;
        const newDef = COMPONENT_REGISTRY[value];
        const newParams: Record<string, any> = {};
        newDef?.params.forEach(p => {
            newParams[p.key] = '';
        });
        onChange({
            ...item,
            component: value,
            params: newDef?.params.length ? newParams : [],
        });
    };

    const handleParamChange = (key: string, value: string) => {
        onChange({ ...item, params: { ...params, [key]: value } });
    };

    const handleDurationChange = (value: string | number) => {
        const num = typeof value === 'number' ? value : parseInt(String(value)) || 0;
        onChange({ ...item, durationSeconds: num });
    };

    const handleAddQrCode = () => {
        setUtmPairs([]);
        onChange({ ...item, qrCode: { ...DEFAULT_QR_CODE } });
    };

    const handleRemoveQrCode = () => {
        const { qrCode: _removed, ...rest } = item;
        onChange(rest as PanelContentWithDuration);
    };

    const handleQrFieldChange = (field: keyof QrCodeConfig, value: any) => {
        if (!item.qrCode) return;
        onChange({ ...item, qrCode: { ...item.qrCode, [field]: value } });
    };

    const syncUtmPairsToParent = (pairs: UtmPair[]) => {
        if (!item.qrCode) return;
        const utmParams: Record<string, string> = {};
        pairs.forEach(({ key, value }) => {
            if (key) utmParams[key] = value;
        });
        onChange({ ...item, qrCode: { ...item.qrCode, utmParams } });
    };

    const handleAddUtmPair = () => {
        const newPairs = [...utmPairs, { id: `${Date.now()}`, key: '', value: '' }];
        setUtmPairs(newPairs);
        syncUtmPairsToParent(newPairs);
    };

    const handleRemoveUtmPair = (id: string) => {
        const newPairs = utmPairs.filter(p => p.id !== id);
        setUtmPairs(newPairs);
        syncUtmPairsToParent(newPairs);
    };

    const handleUtmPairChange = (id: string, field: 'key' | 'value', val: string) => {
        const newPairs = utmPairs.map(p => (p.id === id ? { ...p, [field]: val } : p));
        setUtmPairs(newPairs);
        syncUtmPairsToParent(newPairs);
    };

    return (
        <Card withBorder padding="sm" radius="md">
            <Stack gap="sm">
                {/* Header row: component select + duration + action icons */}
                <Group gap="sm" wrap="nowrap" align="flex-end">
                    <Select
                        label="Component"
                        data={componentOptions}
                        value={item.component}
                        onChange={handleComponentChange}
                        style={{ flex: 1 }}
                        allowDeselect={false}
                    />
                    <NumberInput
                        label="Duration (s)"
                        description="0 = indefinite"
                        value={item.durationSeconds}
                        onChange={handleDurationChange}
                        min={0}
                        w={130}
                    />
                    <Group gap={4} pb={4}>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={onMoveUp}
                            disabled={index === 0}
                            title="Move up"
                        >
                            <IconChevronUp size={16} />
                        </ActionIcon>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={onMoveDown}
                            disabled={index === totalItems - 1}
                            title="Move down"
                        >
                            <IconChevronDown size={16} />
                        </ActionIcon>
                        <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={onRemove}
                            title="Remove"
                        >
                            <IconTrash size={16} />
                        </ActionIcon>
                    </Group>
                </Group>

                {/* Component-specific params */}
                {componentDef && componentDef.params.length > 0 && (
                    <Stack gap="xs">
                        {componentDef.params.map(param => (
                            <TextInput
                                key={param.key}
                                label={param.label}
                                placeholder={param.placeholder}
                                value={params[param.key] ?? ''}
                                onChange={e => handleParamChange(param.key, e.currentTarget.value)}
                            />
                        ))}
                    </Stack>
                )}

                {/* Live iframe refresh (URL Marquee only) */}
                {isUrlMarquee && (
                    <Button
                        variant="light"
                        size="xs"
                        color="gray"
                        leftSection={<IconRefresh size={14} />}
                        onClick={handleRefreshIframe}
                        loading={isReloading}
                        disabled={!deviceId || !marqueeUrl}
                        style={{ alignSelf: 'flex-start' }}
                    >
                        Refresh iframe
                    </Button>
                )}

                {/* QR Code section */}
                {!item.qrCode ? (
                    <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<IconQrcode size={14} />}
                        onClick={handleAddQrCode}
                        color="gray"
                        style={{ alignSelf: 'flex-start' }}
                    >
                        Add QR Code
                    </Button>
                ) : (
                    <>
                        <Divider
                            label={
                                <Group gap={6}>
                                    <IconQrcode size={14} />
                                    <Text size="xs" fw={500}>
                                        QR Code
                                    </Text>
                                </Group>
                            }
                            labelPosition="left"
                        />
                        <Stack gap="sm">
                            <Group grow>
                                <TextInput
                                    label="Position X"
                                    placeholder="e.g. 1105px or 50%"
                                    value={item.qrCode.x}
                                    onChange={e => handleQrFieldChange('x', e.currentTarget.value)}
                                />
                                <TextInput
                                    label="Position Y"
                                    placeholder="e.g. 60px or 10%"
                                    value={item.qrCode.y}
                                    onChange={e => handleQrFieldChange('y', e.currentTarget.value)}
                                />
                            </Group>
                            <Group grow>
                                <TextInput
                                    label="Size"
                                    placeholder="e.g. 250px or 25%"
                                    value={item.qrCode.size}
                                    onChange={e => handleQrFieldChange('size', e.currentTarget.value)}
                                />
                                <NumberInput
                                    label="Margin (px)"
                                    value={item.qrCode.marginSize}
                                    onChange={val =>
                                        handleQrFieldChange(
                                            'marginSize',
                                            typeof val === 'number' ? val : parseInt(String(val)) || 0
                                        )
                                    }
                                    min={0}
                                />
                            </Group>
                            <TextInput
                                label="Target URL"
                                placeholder="https://example.com or myapp://deep/link"
                                value={item.qrCode.target}
                                onChange={e => handleQrFieldChange('target', e.currentTarget.value)}
                            />

                            {/* UTM Params */}
                            <Stack gap="xs">
                                <Text size="xs" fw={500} c="dimmed">
                                    UTM Params
                                </Text>
                                {utmPairs.map(pair => (
                                    <Group key={pair.id} gap="xs" wrap="nowrap">
                                        <TextInput
                                            placeholder="Key (e.g. utmCampaign)"
                                            value={pair.key}
                                            onChange={e =>
                                                handleUtmPairChange(pair.id, 'key', e.currentTarget.value)
                                            }
                                            style={{ flex: 1 }}
                                        />
                                        <Text c="dimmed" size="sm">
                                            =
                                        </Text>
                                        <TextInput
                                            placeholder="Value"
                                            value={pair.value}
                                            onChange={e =>
                                                handleUtmPairChange(pair.id, 'value', e.currentTarget.value)
                                            }
                                            style={{ flex: 1 }}
                                        />
                                        <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            onClick={() => handleRemoveUtmPair(pair.id)}
                                        >
                                            <IconX size={14} />
                                        </ActionIcon>
                                    </Group>
                                ))}
                                <Button
                                    variant="subtle"
                                    size="xs"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={handleAddUtmPair}
                                    color="gray"
                                    style={{ alignSelf: 'flex-start' }}
                                >
                                    Add UTM Param
                                </Button>
                            </Stack>

                            <Button
                                variant="subtle"
                                size="xs"
                                color="red"
                                onClick={handleRemoveQrCode}
                                style={{ alignSelf: 'flex-start' }}
                            >
                                Remove QR Code
                            </Button>
                        </Stack>
                    </>
                )}
            </Stack>
        </Card>
    );
};

export default CarouselItemEditor;
